/** Configurações: idioma + áudio + acessibilidade (UI scale, reduced motion) + controles + reset. */
import { i18n, LOCALE_NAMES } from "../i18n/i18n";
import type { AudioManager, AudioChannel } from "../audio/AudioManager";
import { CAM_ZOOMS, type Accessibility, type UiScaleKey } from "../core/Accessibility";
import { quality, type QualityPreset } from "../core/Quality";

export class SettingsPanel {
  private readonly root: HTMLDivElement;
  private readonly titleEl: HTMLElement;
  private readonly langLabel: HTMLElement;
  private readonly select: HTMLSelectElement;
  private readonly audioTitle: HTMLElement;
  private readonly sliders: Record<AudioChannel, HTMLInputElement>;
  private readonly sliderLabels: Record<AudioChannel, HTMLElement>;
  private readonly controlsTitle: HTMLElement;
  private readonly controlsList: HTMLElement;
  private readonly backBtn: HTMLButtonElement;

  private readonly accessTitle: HTMLElement;
  private readonly uiScaleLabel: HTMLElement;
  private readonly motionBtn: HTMLButtonElement;
  private readonly stickBtn: HTMLButtonElement;
  private readonly camLabel: HTMLElement;
  private readonly graphicsTitle: HTMLElement;
  private readonly backupTitle: HTMLElement;
  private readonly backupHint: HTMLElement;
  private readonly backupCode: HTMLTextAreaElement;
  private readonly backupExport: HTMLButtonElement;
  private readonly backupImport: HTMLButtonElement;

  constructor(
    uiRoot: HTMLElement,
    private readonly handlers: {
      onBack: () => void;
      onUiScale: (k: UiScaleKey) => void;
      onReducedMotion: (v: boolean) => void;
      onStickSide?: (s: "left" | "right") => void; // 10.5Y
      onCamZoom?: (z: number) => void; // 10.6C
      onQuality: (p: QualityPreset) => void;
      onExportSave?: () => string; // 10.5W
      onImportSave?: (code: string) => boolean;
    },
    private readonly audio: AudioManager,
    private readonly access: Accessibility,
  ) {
    this.root = document.createElement("div");
    this.root.className = "overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel s-panel">
        <h2 class="s-title"></h2>
        <div class="settings-row">
          <label class="s-lang-label"></label>
          <select class="s-select"></select>
        </div>
        <div class="settings-row">
          <label class="s-audio-title"></label>
          <div class="audio-row"><span class="a-lbl" data-c="master"></span><input type="range" min="0" max="100" class="a-slider" data-c="master"></div>
          <div class="audio-row"><span class="a-lbl" data-c="music"></span><input type="range" min="0" max="100" class="a-slider" data-c="music"></div>
          <div class="audio-row"><span class="a-lbl" data-c="sfx"></span><input type="range" min="0" max="100" class="a-slider" data-c="sfx"></div>
        </div>
        <div class="settings-row">
          <label class="s-access-title"></label>
          <div class="uiscale-row">
            <span class="s-uiscale-label"></span>
            <div class="uiscale-btns">
              <button class="fbtn" data-us="small"></button>
              <button class="fbtn" data-us="default"></button>
              <button class="fbtn" data-us="large"></button>
            </div>
          </div>
          <button class="btn s-motion" data-a="motion"></button>
          <button class="btn s-stick" data-a="stick"></button>
          <div class="uiscale-row">
            <span class="s-cam-label"></span>
            <div class="uiscale-btns">
              <button class="fbtn" data-cz="0"></button>
              <button class="fbtn" data-cz="1"></button>
              <button class="fbtn" data-cz="2"></button>
              <button class="fbtn" data-cz="3"></button>
            </div>
          </div>
        </div>
        <div class="settings-row">
          <label class="s-graphics-title"></label>
          <div class="uiscale-btns">
            <button class="fbtn" data-q="low"></button>
            <button class="fbtn" data-q="medium"></button>
            <button class="fbtn" data-q="high"></button>
          </div>
        </div>
        <div class="settings-row">
          <label class="s-controls-title"></label>
          <div class="controls-list"></div>
        </div>
        <div class="settings-row">
          <label class="s-backup-title"></label>
          <div class="s-backup-hint"></div>
          <textarea class="s-backup-code" rows="2" spellcheck="false"></textarea>
          <div class="uiscale-btns">
            <button class="btn" data-a="bkexport"></button>
            <button class="btn" data-a="bkimport"></button>
          </div>
        </div>
        <div class="menu-btns"><button class="btn primary" data-a="back"></button></div>
      </div>`;
    uiRoot.appendChild(this.root);

    this.titleEl = this.root.querySelector(".s-title")!;
    this.langLabel = this.root.querySelector(".s-lang-label")!;
    this.select = this.root.querySelector(".s-select")!;
    this.audioTitle = this.root.querySelector(".s-audio-title")!;
    const slider = (c: AudioChannel) => this.root.querySelector<HTMLInputElement>(`.a-slider[data-c="${c}"]`)!;
    const label = (c: AudioChannel) => this.root.querySelector<HTMLElement>(`.a-lbl[data-c="${c}"]`)!;
    this.sliders = { master: slider("master"), music: slider("music"), sfx: slider("sfx") };
    this.sliderLabels = { master: label("master"), music: label("music"), sfx: label("sfx") };
    this.select.id = "vh-settings-language";
    this.langLabel.setAttribute("for", this.select.id);
    for (const channel of ["master", "music", "sfx"] as AudioChannel[]) {
      this.sliderLabels[channel].id = `vh-audio-label-${channel}`;
      this.sliders[channel].setAttribute("aria-labelledby", this.sliderLabels[channel].id);
    }
    this.accessTitle = this.root.querySelector(".s-access-title")!;
    this.uiScaleLabel = this.root.querySelector(".s-uiscale-label")!;
    this.motionBtn = this.root.querySelector('[data-a="motion"]')!;
    this.stickBtn = this.root.querySelector('[data-a="stick"]')!;
    this.stickBtn.onclick = () => {
      const next = this.access.stickSide === "right" ? "left" : "right";
      this.handlers.onStickSide?.(next);
      this.render();
    };
    this.camLabel = this.root.querySelector(".s-cam-label")!;
    for (let k = 0; k < CAM_ZOOMS.length; k++) {
      this.root.querySelector<HTMLButtonElement>(`.fbtn[data-cz="${k}"]`)!.onclick = () => {
        this.handlers.onCamZoom?.(CAM_ZOOMS[k]);
        this.render();
      };
    }
    this.graphicsTitle = this.root.querySelector(".s-graphics-title")!;
    this.controlsTitle = this.root.querySelector(".s-controls-title")!;
    this.controlsList = this.root.querySelector(".controls-list")!;
    this.backBtn = this.root.querySelector('[data-a="back"]')!;
    // 10.5W — backup do progresso (código para levar o save para outro aparelho)
    this.backupTitle = this.root.querySelector(".s-backup-title")!;
    this.backupHint = this.root.querySelector(".s-backup-hint")!;
    this.backupCode = this.root.querySelector(".s-backup-code")!;
    this.backupCode.id = "vh-settings-backup";
    this.backupTitle.setAttribute("for", this.backupCode.id);
    this.backupExport = this.root.querySelector('[data-a="bkexport"]')!;
    this.backupImport = this.root.querySelector('[data-a="bkimport"]')!;
    this.backupExport.onclick = () => {
      const code = this.handlers.onExportSave?.() ?? "";
      this.backupCode.value = code;
      this.backupCode.select();
      try { void navigator.clipboard?.writeText(code); } catch { /* sem clipboard: fica no campo */ }
      this.backupHint.textContent = i18n.t("backup.copied");
    };
    this.backupImport.onclick = () => {
      const ok = this.handlers.onImportSave?.(this.backupCode.value) ?? false;
      this.backupHint.textContent = i18n.t(ok ? "backup.restored" : "backup.invalid");
    };

    this.buildOptions();
    (Object.keys(this.sliders) as AudioChannel[]).forEach((c) => {
      this.sliders[c].oninput = () => this.audio.setVolume(c, Number(this.sliders[c].value) / 100);
    });
    this.select.onchange = () => i18n.setLocale(this.select.value as never);
    for (const k of ["small", "default", "large"] as UiScaleKey[]) {
      this.root.querySelector<HTMLButtonElement>(`.fbtn[data-us="${k}"]`)!.onclick = () => {
        this.handlers.onUiScale(k);
        this.render();
      };
    }
    this.motionBtn.onclick = () => {
      this.handlers.onReducedMotion(!this.access.reducedMotion);
      this.render();
    };
    for (const p of ["low", "medium", "high"] as QualityPreset[]) {
      this.root.querySelector<HTMLButtonElement>(`.fbtn[data-q="${p}"]`)!.onclick = () => {
        this.handlers.onQuality(p);
        this.render();
      };
    }
    this.backBtn.onclick = () => this.handlers.onBack();

    i18n.onChange(() => this.render());
    this.render();
  }

  private buildOptions(): void {
    this.select.innerHTML = "";
    for (const locale of i18n.availableLocales()) {
      const opt = document.createElement("option");
      opt.value = locale;
      opt.textContent = LOCALE_NAMES[locale];
      this.select.appendChild(opt);
    }
    this.select.value = i18n.locale;
  }

  private render(): void {
    this.titleEl.textContent = i18n.t("settings.title");
    this.langLabel.textContent = i18n.t("settings.language");
    this.audioTitle.textContent = i18n.t("settings.audio");
    this.sliderLabels.master.textContent = i18n.t("audio.master");
    this.sliderLabels.music.textContent = i18n.t("audio.music");
    this.sliderLabels.sfx.textContent = i18n.t("audio.sfx");
    // acessibilidade
    this.accessTitle.textContent = i18n.t("settings.accessibility");
    this.uiScaleLabel.textContent = i18n.t("settings.uiScale");
    for (const k of ["small", "default", "large"] as UiScaleKey[]) {
      const b = this.root.querySelector<HTMLButtonElement>(`.fbtn[data-us="${k}"]`)!;
      b.textContent = i18n.t(`settings.uiScale${k[0].toUpperCase()}${k.slice(1)}`);
      b.classList.toggle("active", this.access.uiScale === k);
      b.setAttribute("aria-pressed", String(this.access.uiScale === k));
    }
    this.motionBtn.textContent = `${i18n.t("settings.reducedMotion")}: ${this.access.reducedMotion ? i18n.t("common.on") : i18n.t("common.off")}`;
    this.motionBtn.classList.toggle("active", this.access.reducedMotion);
    this.motionBtn.setAttribute("aria-pressed", String(this.access.reducedMotion));
    // 10.5Y — lado do direcional
    this.stickBtn.textContent = `${i18n.t("settings.stickSide")}: ${i18n.t(this.access.stickSide === "right" ? "settings.stickRight" : "settings.stickLeft")}`;
    // 10.6C — distância da câmera
    this.camLabel.textContent = i18n.t("settings.camera");
    for (let k = 0; k < CAM_ZOOMS.length; k++) {
      const b = this.root.querySelector<HTMLButtonElement>(`.fbtn[data-cz="${k}"]`)!;
      b.textContent = i18n.t(`settings.cam${k}`);
      b.classList.toggle("active", Math.abs(this.access.camZoom - CAM_ZOOMS[k]) < 0.02);
      b.setAttribute("aria-pressed", String(Math.abs(this.access.camZoom - CAM_ZOOMS[k]) < 0.02));
    }
    // 10.5W — backup do progresso
    this.backupTitle.textContent = i18n.t("backup.title");
    if (!this.backupHint.textContent) this.backupHint.textContent = i18n.t("backup.hint");
    this.backupCode.placeholder = i18n.t("backup.placeholder");
    this.backupExport.textContent = i18n.t("backup.export");
    this.backupImport.textContent = i18n.t("backup.import");
    // qualidade gráfica (10.4X)
    this.graphicsTitle.textContent = i18n.t("settings.graphics");
    for (const p of ["low", "medium", "high"] as QualityPreset[]) {
      const b = this.root.querySelector<HTMLButtonElement>(`.fbtn[data-q="${p}"]`)!;
      b.textContent = i18n.t(`graphics.${p}`);
      b.classList.toggle("active", quality.name === p);
      b.setAttribute("aria-pressed", String(quality.name === p));
    }
    this.sliders.master.value = String(Math.round(this.audio.getVolume("master") * 100));
    this.sliders.music.value = String(Math.round(this.audio.getVolume("music") * 100));
    this.sliders.sfx.value = String(Math.round(this.audio.getVolume("sfx") * 100));
    this.controlsTitle.textContent = i18n.t("settings.controls");
    this.backBtn.textContent = i18n.t("menu.back");
    this.select.value = i18n.locale;
    // Mobile-first: joystick + botões touch (ataque é AUTOMÁTICO). Teclado/mouse só DEV.
    this.controlsList.innerHTML =
      `<div><b>${i18n.t("controls.stick")}</b> — ${i18n.t("controls.move")}</div>` +
      `<div><b>${i18n.t("controls.attack")}</b> — ${i18n.t("controls.auto")}</div>` +
      `<div><b>${i18n.t("touch.hook")}</b> — ${i18n.t("controls.hook")}</div>` +
      `<div><b>${i18n.t("touch.sprint")}</b> — ${i18n.t("controls.sprint")}</div>` +
      `<div><b>${i18n.t("touch.interact")}</b> — ${i18n.t("controls.interact")}</div>` +
      `<div class="dev-note">${i18n.t("controls.devHint")}</div>`;
  }

  show(): void {
    this.render(); // sincroniza sliders com o estado atual do áudio
    this.root.hidden = false;
  }
  hide(): void {
    this.root.hidden = true;
  }
}
